import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";

/**
 * Barchart component
 * Renders a horizontal bar chart using react-apexcharts based on incoming data.
 *
 * Props:
 * - `chartdata` (object): expected shape { district_names: [], cancer_names: [], cases_count: [] }
 * - `displaychart` (object): configuration with a `value` key that selects chart type (e.g., 'population')
 *
 * The component derives `categories` and `cases` from `chartdata` depending on `displaychart.value`.
 * It validates arrays before configuring the chart to avoid runtime errors.
 */
const Barchart = ({ chartdata, displaychart }) => {
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState([]);
  const [chartHeight, setChartHeight] = useState(400); // default height

  // `displaychart.value` selects which category list to use (districts vs cancers)
  const { value } = displaychart || {};

  // Derived values (used for initial render checks). Detailed derivation happens inside useEffect.
  // `chartType`: normalized display type string (e.g., 'population')
  const chartType = String(value).toLowerCase();

  // `categories`: array of labels for the x-axis (district names or cancer names)
  const categories =
    chartType === "population"
      ? chartdata?.district_names
      : chartdata?.cancer_names;

  useEffect(() => {
    // derive values inside effect so deps can be simple and correct
    if (!chartdata) return;

    const chartType = String(value).toLowerCase();
    const categories =
      chartType === "population"
        ? chartdata?.district_names
        : chartdata?.cancer_names;
    const cases = chartdata?.cases_count;

    if (!categories || !Array.isArray(categories) || categories.length === 0)
      return;
    if (!cases || !Array.isArray(cases) || cases.length === 0) return;

    const dynamicHeight = Math.max(categories.length * 30, 250);
    setChartHeight(dynamicHeight);

    setChartSeries([
      {
        name: "Cases",
        data: cases,
      },
    ]);

    setChartOptions({
      chart: { type: "bar", toolbar: { show: true } },
      xaxis: {
        categories: categories,
        title: { text: "Cases" },
      },
      yaxis: {
        title: {
          text: chartType === "population" ? "Districts" : "Cancer",
        },
        labels: {
          style: { fontSize: "12px" },
        },
      },
      dataLabels: { enabled: false },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "70%",
        },
      },
    });
  }, [chartdata, value]);

  if (
    !chartdata ||
    !categories ||
    !Array.isArray(categories) ||
    categories.length === 0
  ) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border center text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <Chart
        options={chartOptions}
        series={chartSeries}
        type="bar"
        width="100%"
        height={chartHeight}
      />
    </div>
  );
};

export default Barchart;
